import { useEffect, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'

import placeholderAvatar from 'assets/images/user_placeholder.png'
import { IconStatsClose } from 'assets/svg/sprite'
import { ShowMore } from 'components/ShowMore/ShowMore'
import { getStoryBackground } from 'components/UI/BackgroundPicker/BackgroundPicker'
import { ThreeDotsTrigger } from 'components-ref/atoms'
import { LastPostInfo, PostPoll } from 'components-ref/molecules'
import { usePreviewContext } from 'context/previewContext'
import PreviewActions from 'features/Preview/PreviewActions/PreviewActions'
import { useOrientation } from 'helpers/helperHooks'
import { useAnswerPoll } from 'helpers/hooks'
import { extractMedia } from 'helpers/media'
import { getCachedBlob } from 'helpers/mediaPrefetch'
import { useUserProfile } from 'store'
import { type IPost } from 'types/interfaces/ITypes'

interface Props {
  post: IPost
  showControls: boolean
  isFirstPost: boolean
  hasNextPage: boolean
  minimizeFn: (_fileIndex: number, _type: string) => void
  toggleControls: () => void
  isLastPost?: boolean
  setIsOptionsNavOpen?: (val: boolean) => void
}

const SinglePostPreviewPoll = ({
  post,
  showControls,
  toggleControls,
  hasNextPage,
  isLastPost = false,
  setIsOptionsNavOpen
}: Props) => {
  const deviceOrientation = useOrientation()
  const { t } = useTranslation()
  const userData = useUserProfile()!

  const {
    query,
    setCommentsActive,
    minimized,
    clearModal
  } = usePreviewContext()

  const { setPollAnswer } = useAnswerPoll(query, post.id)

  const isPortrait =
    deviceOrientation?.type?.includes('portrait') ?? true

  const [optimisticPoll, setOptimisticPoll] =
    useState(post.poll)

  const postFile = useMemo(
    () => ({
      userId: post.user_id,
      name: post.user?.display_name,
      text: post.body,
      mentions: post.tags,
      media: extractMedia(post),
      minimizedName: post.user?.display_name,
      minimizedAvatar:
        post.user?.cropped_avatar?.url ??
        post.user?.avatar?.url ??
        placeholderAvatar
    }),
    [
      post,
      post.user_id,
      post.user?.display_name,
      post.user?.cropped_avatar?.url,
      post.user?.avatar?.url,
      post.body,
      post.tags
    ]
  )

  useEffect(() => {
    setOptimisticPoll(post.poll)
  }, [post.poll])

  if (!optimisticPoll) return null

  const poll = optimisticPoll

  const hasBackgroundImage = Boolean(poll.background_src)

  const thumbnailUrl =
    poll.background_src_thumb ??
    poll.background_src

  const fullImageUrl = poll.background_src

  const cachedThumbnail = thumbnailUrl
    ? getCachedBlob(thumbnailUrl)
    : null

  const cachedFullImage = fullImageUrl
    ? getCachedBlob(fullImageUrl)
    : null

  const backgroundUrl =
    cachedThumbnail ??
    thumbnailUrl

  const foregroundUrl =
    cachedFullImage ??
    fullImageUrl ??
    backgroundUrl
useEffect(() => {
  if (!hasBackgroundImage) return

  const thumbnailIsBlob =
    typeof backgroundUrl === 'string' &&
    backgroundUrl.startsWith('blob:')

  const foregroundIsBlob =
    typeof foregroundUrl === 'string' &&
    foregroundUrl.startsWith('blob:')

  console.groupCollapsed(
    `[PollBlobCache] Post ${post.id}`
  )

  console.table({
    thumbnail: {
      originalUrl: thumbnailUrl,
      cachedBlobFound: Boolean(cachedThumbnail),
      usingBlob: thumbnailIsBlob,
      renderedUrl: backgroundUrl
    },
    fullImage: {
      originalUrl: fullImageUrl,
      cachedBlobFound: Boolean(cachedFullImage),
      usingBlob: foregroundIsBlob,
      renderedUrl: foregroundUrl
    }
  })

  console.log(
    '[PollBlobCache] Background source:',
    thumbnailIsBlob ? 'CACHED BLOB' : 'NETWORK URL'
  )

  console.log(
    '[PollBlobCache] Foreground source:',
    foregroundIsBlob ? 'CACHED BLOB' : 'NETWORK URL'
  )

  console.groupEnd()
}, [
  post.id,
  hasBackgroundImage,
  thumbnailUrl,
  fullImageUrl,
  cachedThumbnail,
  cachedFullImage,
  backgroundUrl,
  foregroundUrl
])
  const backgroundImage = hasBackgroundImage
    ? undefined
    : getStoryBackground(poll.background_color)

  return (
    <>
      <div
        className="preview__close preview__close__poll"
        onClick={() => {
          clearModal()
        }}
      >
        {minimized ? (
          <IconStatsClose color="#A3A3A3" />
        ) : (
          <IconStatsClose />
        )}
      </div>

      {showControls && (
        <ThreeDotsTrigger
          onClick={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
            e.stopPropagation()
            setIsOptionsNavOpen?.(true)
          }}
        />
      )}

      <div className="preview__file--wrapper">
        {showControls && (
          <>
            <ShowMore
              userId={postFile.userId}
              name={postFile.name}
              time={post.created_at}
              text={postFile.text ?? ''}
              mentions={postFile.mentions}
            />

            <PreviewActions
              stats={{
                overallRevenue: post.overall_revenue,
                purchasesCount: post.purchases_count,
                purchasesSum: post.purchases_sum,
                tipsCount: post.tips_count,
                tipsSum: post.tips_sum,
                conversionRate: post.conversion_rate,
                uniqueImpressions: post.unique_impressions,
                views: post.views,
                uniqueViews: post.unique_views,
                price: post.price
              }}
              modelData={{
                postId: post.id,
                modelId: post.user.id,
                avatarSrc:
                  post.user.avatar?.url ??
                  post.user.cropped_avatar?.url ??
                  placeholderAvatar
              }}
              query={query}
              postId={post.id}
              isLiked={post.isLiked}
              likes={post.likes_count}
              comments={post.comment_count}
              setCommentsActive={setCommentsActive}
            />
          </>
        )}

        <div
          onClick={e => {
            e.stopPropagation()
            toggleControls()
          }}
          className={`preview__file preview__file--no-media ${
            isPortrait ? 'portrait' : 'landscape'
          }`}
        >
          {hasBackgroundImage ? (
            <>
              <div
                className="preview__poll-background-blur"
                style={{
                  backgroundImage: backgroundUrl
                    ? `url("${backgroundUrl}")`
                    : undefined
                }}
              />

              <div
                className="preview__poll-background-image"
                style={{
                  backgroundImage: foregroundUrl
                    ? `url("${foregroundUrl}")`
                    : undefined
                }}
              />
            </>
          ) : (
            <div
              className="preview__background"
              style={{ backgroundImage }}
            />
          )}

          <div className="preview__goal-bottom">
            {isLastPost && !hasNextPage && (
              <LastPostInfo isGoal />
            )}

            <div className="preview__poll">
              <PostPoll
                pollData={poll}
                setAnswer={answer => {
                  const previousPoll = optimisticPoll

                  setOptimisticPoll(prev => {
                    if (!prev) return prev

                    if (prev.type === 'ask') {
                      const answerText = String(
                        answer.answer ?? ''
                      )

                      return {
                        ...prev,
                        answer: answerText,
                        is_answered: answerText
                      }
                    }

                    const answerId = answer.answerId

                    if (
                      answerId == null ||
                      answerId === ''
                    ) {
                      return prev
                    }

                    return {
                      ...prev,
                      is_answered: answerId,
                      answers_count:
                        prev.answers_count.map(option =>
                          String(option.id) ===
                          String(answerId)
                            ? {
                                ...option,
                                count:
                                  Number(
                                    option.count ?? 0
                                  ) + 1
                              }
                            : option
                        )
                    }
                  })

                  setPollAnswer.mutate(answer, {
                    onError: () => {
                      setOptimisticPoll(previousPoll)
                    }
                  })
                }}
                isMyPost={userData.id === post.user_id}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
export default SinglePostPreviewPoll